import type { ApiClient } from '@commander/api-client';
import { createApiClient, createAxiosInstance } from '@commander/api-client';
import { buildStoredLocationTypes } from '@commander/utils';
import { createIntegrationsService } from '../../integrations/services/integrations.service';
import type {
  CompanyICP,
  CompanyProfile,
  EmailSettings,
  InviteUser,
  OnboardingFormData,
  User,
} from '../types/onboarding.types';
import {
  convertSizeRangeToJSON,
  formatInviteUsersForAPI,
  formatLocationsForAPI,
} from '../utils/helpers';

/**
 * API response types matching backend structure
 */
interface ApiDataResponse<T> {
  data: T;
  message?: string;
  success?: boolean;
}

/**
 * Upload response type for prospect uploads
 */
interface UploadResponse {
  success: boolean;
  message: string;
  data?: any;
}

/**
 * Factory function to create onboarding API service
 * @param apiClient - ApiClient instance from context (injected)
 * @returns Service object with all API methods
 */
export const createOnboardingService = (apiClient: ApiClient) => ({
  /**
   * Create company profile
   */
  async createCompanyProfile(profile: Partial<CompanyProfile>): Promise<CompanyProfile> {
    const response = await apiClient.post<ApiDataResponse<CompanyProfile>>(
      '/company_profiles',
      profile
    );
    return response;
  },

  /**
   * Update company profile
   */
  async updateCompanyProfile(
    id: string,
    profile: Partial<CompanyProfile>
  ): Promise<CompanyProfile> {
    const response = await apiClient.put<ApiDataResponse<CompanyProfile>>(
      `/company_profiles/${id}`,
      profile
    );
    return response;
  },

  /**
   * Get company profile
   */
  async getCompanyProfile(): Promise<CompanyProfile> {
    const response = await apiClient.get<ApiDataResponse<CompanyProfile>>('/company_profiles');
    return response.data;
  },

  /**
   * Create company ICP
   */
  async createCompanyICP(icp: Partial<CompanyICP>): Promise<CompanyICP> {
    const response = await apiClient.post<ApiDataResponse<CompanyICP>>('/company_icps', icp);
    return response;
  },

  /**
   * Update company ICP fields
   */
  async updateCompanyICPFields(id: string, fields: Partial<CompanyICP>): Promise<CompanyICP> {
    const response = await apiClient.put<ApiDataResponse<CompanyICP>>(
      `/company_icps/${id}`,
      fields
    );
    return response;
  },

  /**
   * Get company ICPs (returns list of company ICPs with location data)
   */
  async getCompanyICPs(): Promise<CompanyICP> {
    const response = await apiClient.get<ApiDataResponse<CompanyICP>>('/company_icps');
    return response.data;
  },

  /**
   * Update user step
   */
  async updateUserStep(stepNumber: number): Promise<void> {
    await apiClient.put<ApiDataResponse<void>>('/users', {
      step_number: stepNumber,
    });
  },

  /**
   * Complete onboarding
   */
  async completeOnboarding(): Promise<void> {
    await apiClient.put<ApiDataResponse<void>>('/users', {
      onboarding_complete: true,
    });
  },

  /**
   * Invite users
   */
  async inviteUsers(inviteData: {
    employees: Array<{ name: string; email: string }>;
  }): Promise<void> {
    await apiClient.post<ApiDataResponse<void>>('/user/invite', inviteData);
  },

  /**
   * Save communication style
   */
  async saveCommunicationStyle(settings: EmailSettings): Promise<ApiDataResponse<void>> {
    const requestData: any = {
      communication_style: settings.communicationStyle,
    };

    // Only include communication_style_text if it has a value
    if (settings.communicationStyleText) {
      requestData.communication_style_text = settings.communicationStyleText;
    }

    const response = await apiClient.put<ApiDataResponse<void>>('/users/settings', requestData);
    return response;
  },

  /**
   * Create email signature in email_signatures table
   * @param name - Signature name
   * @param body - Lexical JSON body
   * @param isDefault - Whether this is the default signature
   */
  async createEmailSignature(
    name: string,
    body: any,
    isDefault = true
  ): Promise<ApiDataResponse<any>> {
    const requestData = {
      name,
      body,
      is_default: isDefault,
    };

    const response = await apiClient.post<ApiDataResponse<any>>(
      '/communications/email_signatures',
      requestData
    );
    return response;
  },

  // Comprehensive save methods that handle multiple API calls

  /**
   * Get current user details
   */
  async getUserDetails(): Promise<User> {
    const response = await apiClient.get<ApiDataResponse<User>>('/user/details');
    return response.data;
  },

  /**
   * Saves company information (company name and domain)
   */
  async saveCompanyInfo(
    formData: OnboardingFormData,
    existingProfile?: CompanyProfile
  ): Promise<CompanyProfile> {
    const companyData = {
      company_name: formData.company,
      domain: formData.domain,
    };

    if (existingProfile?.id) {
      return this.updateCompanyProfile(existingProfile.id, companyData);
    } else {
      return this.createCompanyProfile(companyData);
    }
  },

  /**
   * Saves services to company profile
   */
  async saveServices(
    services: string[],
    existingProfile?: CompanyProfile
  ): Promise<CompanyProfile> {
    if (!existingProfile?.id) {
      // Create profile with just services if it doesn't exist
      return this.createCompanyProfile({
        company_name: '',
        domain: '',
        products: services,
      });
    }

    return this.updateCompanyProfile(existingProfile.id, {
      products: services,
    });
  },

  /**
   * Saves locations to company ICP
   */
  async saveLocations(locations: string[], existingICP?: CompanyICP): Promise<CompanyICP> {
    const formattedLocations = formatLocationsForAPI(
      locations,
      buildStoredLocationTypes(existingICP?.locations)
    );

    if (existingICP?.id) {
      return this.updateCompanyICPFields(existingICP.id, {
        locations: formattedLocations,
      });
    } else {
      return this.createCompanyICP({
        locations: formattedLocations,
      });
    }
  },

  /**
   * Saves company size ranges to company ICP
   */
  async saveCompanySize(sizes: string[], existingICP?: CompanyICP): Promise<CompanyICP> {
    const sizeRangesJSON = sizes
      .map((sizeRange: string) => convertSizeRangeToJSON(sizeRange))
      .filter((range): range is NonNullable<typeof range> => range != null);

    if (existingICP?.id) {
      return this.updateCompanyICPFields(existingICP.id, {
        size_ranges: sizeRangesJSON,
      });
    } else {
      return this.createCompanyICP({
        size_ranges: sizeRangesJSON,
      });
    }
  },

  /**
   * Saves industries to company ICP
   */
  async saveIndustries(industries: string[], existingICP?: CompanyICP): Promise<CompanyICP> {
    const businessSectors = [
      {
        sector: 'CUSTOM',
        subsectors: industries.map((industry) => ({
          name: industry,
        })),
      },
    ];

    if (existingICP?.id) {
      return this.updateCompanyICPFields(existingICP.id, {
        business_sectors: businessSectors,
      });
    } else {
      return this.createCompanyICP({
        business_sectors: businessSectors,
      });
    }
  },

  /**
   * Saves job titles to company ICP
   */
  async saveJobTitles(jobTitles: string[], existingICP?: CompanyICP): Promise<CompanyICP> {
    if (existingICP?.id) {
      return this.updateCompanyICPFields(existingICP.id, {
        job_titles: jobTitles,
      });
    } else {
      return this.createCompanyICP({
        job_titles: jobTitles,
      });
    }
  },

  /**
   * Saves email settings (signature and communication style)
   */
  async saveEmailSettings(emailSettings: EmailSettings): Promise<ApiDataResponse<void>> {
    return this.saveCommunicationStyle(emailSettings);
  },

  /**
   * Saves invited users
   */
  async saveInviteUsers(inviteUsers: InviteUser[]): Promise<void> {
    const inviteData = formatInviteUsersForAPI(inviteUsers);
    await this.inviteUsers(inviteData);
  },

  /**
   * Refresh company profile data
   */
  async refreshCompanyProfile(): Promise<CompanyProfile> {
    return this.getCompanyProfile();
  },

  /**
   * Refresh company ICP data
   */
  async refreshCompanyICP(): Promise<CompanyICP> {
    return this.getCompanyICPs();
  },

  /**
   * Upload CSV file with prospects
   * @param companyId - The company ID from auth context
   * @param file - CSV file to upload
   */
  async uploadProspects(companyId: string, file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('files', file);

    const response = await apiClient.post<UploadResponse>(
      `/companies/${companyId}/company_prospects/upload_unprocessed`,
      formData
    );

    return response;
  },

  async createTwentyCRMApi(params: {
    companyId: string;
    apiKey: string;
    crmUrl: string;
  }): Promise<any> {
    return createIntegrationsService(apiClient).connectTwentyCRM(params);
  },

  /**
   * Get preview prospects for a company
   * @param companyId - The company ID
   * @returns Promise resolving to prospect data
   */
  async previewProspects(companyId: string): Promise<any> {
    const response = await apiClient.get<ApiDataResponse<any>>(
      `/companies/${companyId}/onboarding/preview-prospects`
    );
    return response;
  },
});

// Export type for the service instance
export type OnboardingApi = ReturnType<typeof createOnboardingService>;

// Default export for backward compatibility with existing hooks
// This creates a singleton that uses fetch directly for now
// Components should migrate to using the factory pattern with ApiClient from context

// Create a default instance for backward compatibility
// In production, components should use ApiClientProvider context
const defaultAxiosInstance = createAxiosInstance({
  baseURL: import.meta.env?.VITE_API_BASE_URL
    ? import.meta.env.VITE_API_BASE_URL
    : 'http://localhost:3011/',
  getToken: () => {
    // Get token from cookie or localStorage
    if (typeof document !== 'undefined') {
      const match = document.cookie.match(/(?:^|; )auth_token=([^;]*)/);
      if (match?.[1]) return decodeURIComponent(match[1]);
    }
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('auth_token');
    }
    return null;
  },
});

const defaultApiClient = createApiClient(defaultAxiosInstance);
export const onboardingApi = createOnboardingService(defaultApiClient);

/**
 * Default service instance creator using app configuration
 * @deprecated Use createOnboardingService with ApiClient from context instead
 */
export const useOnboardingService = (): OnboardingApi => {
  return onboardingApi;
};
